import { MonoBehavior } from './core/monoBehavior';
import { Container, Point } from 'pixi.js';
import { addToLevel } from './core/Playable2DRootContainer';
import { GameEvent } from './events/gameEvent';
import { EventName } from './events/eventsName';
import { getScreenSize } from './core/screen';
import { SoundManager } from './core/soundManager';
import { moveSound } from './foundationStreakManager';

import { getDifficultiesList, getSuit, getSuitWinScore } from './deals';
import { areBoxesCollide } from './helpers/collision';
import { DifficultyLevelType, GameName, getFirstDifficulty } from 'constants/difficulty';
import { Stockpile } from './Stockpile';
import { Column } from './Column';
import { Moves } from './Moves';
import { Hints } from './Hints';
import { Foundation } from './Foundation';
import { GamePlace, DropCardPlace, ACES_VALUE } from 'constants/cards';
import { COLUMN_Z_INDEX, MOVE_Z_INDEX } from 'constants/layout';
import { Layout } from './LayoutManager';
import { Card } from 'components/Card';
import {
    AUTOCOMPLETE_TRIGGER_DELAY,
    WIN_POPUP_DELAY,
    CARD_MOVE_TIME,
    AUTOCOMPLETE_MOVE_TIME,
    WIN_POPUP_SPIDER_DELAY,
    CARD_GROUP_DELAY_TIME,
    AUTOPLAY_DELAY,
} from 'constants/move';
import { WastePile } from './WastePile';
import { WinScreen } from './WinScreen';
import { Store } from './store/gameStore';
import { AutocompleteWorker } from './AutocompleteWorker';
import { GAEvent, GAEventNames } from './analytics/events';
import { drawCardsIntro } from './drawCards';
import { loadCardsTable } from './loadTable';
import { FreeCell } from './FreeCell';
import { getOS, isAndroid, isIOS } from './core/display';
import { logWinGame } from './analytics/logWins';
import { MagicWand } from './MagicWand';
import { GameBoardPlace } from './GameBoardPlace';
import { MagicItem, MagicPlace } from './gameMagicWand/magicGroup';
import gsap from 'gsap';
import { preloadGameSounds } from './Sounds';
import { lateAssetLoad } from './preloadGameAssets';
import { GameLog } from './helpers/debug';
import { TimerLocalStorageUpdater } from './utils/timerLocalStorageUpdater';
import { ExternalClickArea } from 'components/ExternalClickArea';
import { BaseStrategy } from './strategies/BaseStrategy';
import { getStrategyViewByGame } from './utils/strategy';
import { TutorialManager } from './gameUI/TutorialManager';
import { ActionType, MissionActionData } from './quests/types';

export class SolitaireGame extends MonoBehavior {
    columns = [] as Column[];
    foundations = [] as Foundation[];
    stockPile: Stockpile;
    wastePile?: WastePile;
    freeCells = [] as FreeCell[];

    cards = [] as Card[];
    moveGroup = [] as Card[];
    keyEventsActive = false; //event is not active until draw is finished (this could be set to false before event is bind)
    gameContainer: Container;

    firstPointerPosition = new Point();
    currentPointerPosition = new Point();

    firstMove: boolean;
    moves: Moves;
    startMoveTime = 0;
    gameOver = false;
    lastZIndex = MOVE_Z_INDEX;
    lastMove = 0;
    autocompleteActivated = false;
    autoPlayActivated = false;
    autocompleteWorker: AutocompleteWorker;
    playDragSound?: number;
    moveBlocked = 0;
    autoPlayMoveCount = 0;
    dragActive = false;
    magicWand: MagicWand;
    winScreen: WinScreen;
    tutorialManager: TutorialManager;

    timerLocalStorageUpdater: TimerLocalStorageUpdater;
    private externalClickArea: ExternalClickArea;
    public gameStrategy?: BaseStrategy;

    public static instance: SolitaireGame;

    constructor(onReady?: () => void) {
        super();
        SolitaireGame.instance = this;
        this.gameStrategy = BaseStrategy.instance;

        this.gameStrategy?.init(this, getStrategyViewByGame(Store.name, this));

        this.newGame = this.newGame.bind(this);
        this.onPointerup = this.onPointerup.bind(this);
        this.onAutoPlay = this.onAutoPlay.bind(this);
        this.onDragCard = this.onDragCard.bind(this);
        this.onDragDisabledCard = this.onDragDisabledCard.bind(this);
        this.dragCard = this.dragCard.bind(this);
        this.revealCard = this.revealCard.bind(this);
        this.refreshBoard = this.refreshBoard.bind(this);
        this.runAutocomplete = this.runAutocomplete.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.triggerAutocompleteMove = this.triggerAutocompleteMove.bind(this);
        this.onAutoPlayFinished = this.onAutoPlayFinished.bind(this);
        this.makeNextAutocompleteMove = this.makeNextAutocompleteMove.bind(this);

        this.gameContainer = new Container();
        this.gameContainer.name = 'gameContainer';
        this.gameContainer.sortableChildren = true;
        this.firstMove = false;
        GameEvent.add(EventName.DRAG_CARD, this.onDragCard);

        GameEvent.add(EventName.DRAG_DISABLED_CARD, this.onDragDisabledCard);
        GameEvent.add(EventName.AUTO_PLAY, this.onAutoPlay);
        GameEvent.add(EventName.REVEAL_CARD, this.revealCard);
        GameEvent.add(EventName.REFRESH, this.refreshBoard);
        GameEvent.add(EventName.RUN_AUTOCOMPLETE, this.runAutocomplete);
        GameEvent.add(EventName.AUTOCOMPLETE_MOVE_FINISHED, this.triggerAutocompleteMove);
        GameEvent.add(EventName.AUTOPLAY_MOVE_FINISHED, this.onAutoPlayFinished);
        GameEvent.add(EventName.ADS_GRANTED, () => {
            GameEvent.emit(EventName.BLOCK_ACTION_BUTTONS, 1);
            this.refreshBoard();
        });
        GameEvent.add(EventName.BLOCK_KEYBOARD, () => {
            this.keyEventsActive = false;
        });
        GameEvent.add(EventName.UNBLOCK_KEYBOARD, () => {
            this.keyEventsActive = true;
        });
        GameEvent.add(EventName.START_TUTORIAL, this.startTutorial.bind(this));
        this.autocompleteWorker = new AutocompleteWorker(this);

        this.stockPile = new Stockpile(this);

        if (!Store.isKlondike) {
            this.wastePile = new WastePile(this);

            if (this.gameStrategy?.IsShowWastePilePlaceholder) {
                this.wastePile.showPlaceholder();
            }
        }

        this.moves = new Moves(this);
        new Hints(this);
        this.magicWand = new MagicWand(this);
        this.winScreen = new WinScreen();
        this.timerLocalStorageUpdater = new TimerLocalStorageUpdater(this);

        this.externalClickArea = new ExternalClickArea();

        addToLevel(this.externalClickArea.bounds);
        this.externalClickArea.bounds.addEventListener('pointerdown', this.onExternalClick.bind(this));
        this.tutorialManager = new TutorialManager(this);

        onReady?.();
    }

    onExternalClick() {
        this.moveGroup.forEach(card => card.resetHighlight());
        this.moveGroup = [];
    }

    onKeyDown(e: KeyboardEvent) {
        if (Store.tutorialActive) {
            if (e.code === 'Space') {
                e.preventDefault();
                e.stopPropagation();
            }
            return;
        }
        if (!this.keyEventsActive) return;
        if (e.code === 'KeyP') {
            if (Store.gameActive) Store.pauseGame();
            else Store.resumeGame();
        }
        if (e.code === 'Space' && this.stockPile && Store.gameActive) {
            e.preventDefault();
            e.stopPropagation();
            this.stockPile.deal();
        }
        if (isIOS() || isAndroid()) return;

        const isCtrlZ = getOS() === 'desktop_mac' ? e.metaKey : e.ctrlKey;
        if (e.code === 'KeyZ' && isCtrlZ) {
            e.preventDefault();
            e.stopPropagation();
            GameEvent.emit(EventName.CTR_Z);
        }
    }
    runAutocomplete() {
        GameEvent.emit(EventName.SET_UNDO_DISABLED, true);
        GameEvent.emit(EventName.SET_HINT_DISABLED, true);
        GameEvent.emit(EventName.SET_WAND_DISABLED, true);

        this.stockPile.block();
        this.blockMove(Infinity);
        if (Store.isSpider) {
            this.autocompleteWorker.makeMove();
            this.autocompleteActivated = false;
        }
        this.triggerAutocompleteMove();
    }
    makeNextAutocompleteMove() {
        this.autocompleteActivated = true;

        const move = this.autocompleteWorker.makeMove();
        if (move) {
            switch (Store.name) {
                case GameName.KLONDIKE:
                case GameName.FREECELL:
                    setTimeout(this.makeNextAutocompleteMove, AUTOCOMPLETE_TRIGGER_DELAY);
                    break;
            }
        } else {
            this.checkWinCondition();
        }
    }

    onAutoPlayFinished() {
        this.refreshAllHighlight();
        if (Store.isSpider) {
            this.refreshBoard();
        }
        this.autoPlayActivated = false;
        return;
    }

    triggerAutocompleteMove() {
        switch (Store.name) {
            case GameName.KLONDIKE:
            case GameName.FREECELL:
            case GameName.PYRAMID:
                if (this.autocompleteActivated) return;

                this.autocompleteActivated = true;
                this.makeNextAutocompleteMove();
                break;
            case GameName.SPIDER:
                this.autocompleteActivated = true;

                const move = this.autocompleteWorker.makeMove();
                if (!move) {
                    this.checkWinCondition();
                }
        }
        if (!this.autocompleteActivated) {
            this.refreshBoard();
            this.refreshAllHighlight();

            return;
        }
    }

    revealCard(cardToActivate: Card) {
        this.moves.addRevealCard(cardToActivate);
    }

    onAutoPlay({ card, e }: { card: Card; e: PointerEvent }) {
        this.blockMove(CARD_MOVE_TIME * 1000);
        this.createMoveGroup(card);
        this.startMoveTime = performance.now();
        this.firstPointerPosition = new Point();
        this.currentPointerPosition = new Point();
        this.lastMove = performance.now();
        setTimeout(() => {
            this.dropCard(true);
        }, 100);
    }

    onDragCard({ card, e }: { card: Card; e: PointerEvent }) {
        if (this.isMoveBlocked) return;
        if (this.autoPlayActivated) return;
        if (this.dragActive) return;

        console.log(`[PYRAMID DRAG] Starting drag for card: ${card.code}(${card.value})`);

        this.createMoveGroup(card);

        if (this.gameStrategy?.onDragCard(this.moveGroup)) return;

        this.startMoveTime = performance.now();

        const now = performance.now();
        if (now - this.lastMove < 10) return;
        this.startDragSound();

        Store.startGame();

        this.lastMove = now;
        const { width, height } = getScreenSize();

        const pointerPos = {
            x: -width / 2 + e.clientX,
            y: -height / 2 + e.clientY,
        };

        this.firstPointerPosition = new Point(pointerPos.x, pointerPos.y);
        this.currentPointerPosition = new Point(pointerPos.x, pointerPos.y);
        addEventListener('pointermove', this.dragCard);
        addEventListener('pointerup', this.onPointerup);
    }

    refreshHighlight(card: Card) {
        if (Store.hasHighlighting()) {
            this.columns.forEach((col) => {
                if (!col.cards.find((c) => c.code === card.code)) {
                    col.showHighlight(col.lastCard, true);
                }
            });
        }
    }

    createMoveGroup(card: Card) {
        console.log(`[PYRAMID MOVE] Creating move group for card: ${card.code}(${card.value})`);
        
        if (this.gameStrategy) {
            this.gameStrategy.onCreateMoveGroup(this.moveGroup, card);
        } else {
            this.moveGroup = [];

            // Стандартная логика для других игр
            if (card.place.cardPlace === GamePlace.TABLEAU) {
                const originColumn = this.columns[card.place.value];
                let allGroupAdded = false;
                for (let index = originColumn.length - 1; index >= 0; index--) {
                    const columnCard = originColumn.cards[index];
                    if (!allGroupAdded) {
                        this.moveGroup.push(columnCard);
                    }
                    if (columnCard._id === card._id) allGroupAdded = true;
                }
                this.moveGroup.reverse();
            } else if (card.place.cardPlace === GamePlace.WASTE_PILE) {
                const dragCard = this.wastePile?.lastCard;
                if (!dragCard) return;
                this.moveGroup.push(dragCard);
            } else if (card.place.cardPlace === GamePlace.FOUNDATION) {
                const dragCard = this.foundations[card.place.value].lastCard;
                if (!dragCard) return;
                this.moveGroup.push(dragCard);
            } else if (card.place.cardPlace === GamePlace.FREECELL) {
                const dragCard = this.freeCells[card.place.value].lastCard;
                if (!dragCard) return;
                this.moveGroup.push(dragCard);
            }
        }

        console.log(`[PYRAMID MOVE] Move group created with ${this.moveGroup.length} cards:`, this.moveGroup.map(c => `${c.code}(${c.value})`));

        this.moveGroup.forEach((moveCard) => {
            moveCard.takeUp();
        });
    }

    dragCard(e: PointerEvent) {
        if (this.isMoveBlocked) return;
        const { width, height } = getScreenSize();

        const pos = {
            x: -width / 2 + e.clientX,
            y: -height / 2 + e.clientY,
        };

        this.currentPointerPosition = new Point(pos.x, pos.y);

        if (!this.dragActive) {
            const distance = Math.sqrt(
                Math.pow(this.currentPointerPosition.x - this.firstPointerPosition.x, 2) +
                    Math.pow(this.currentPointerPosition.y - this.firstPointerPosition.y, 2)
            );
            if (distance > Layout.getCardSize().width * 0.15) {
                this.firstPointerPosition = this.currentPointerPosition.clone();
                this.dragActive = true;
            } else return;
        }

        const moveDiff = this.getMoveDiff();
        this.moveGroup.forEach((card, index) => {
            card.cardDisplay.container.position = moveDiff;
            card.container.zIndex = this.lastZIndex + index;
        });
    }

    onDragDisabledCard({ card, e }: { card: Card; e: PointerEvent }) {
        if (this.isMoveBlocked) return;
        if (this.autoPlayActivated) return;
        if (this.dragActive) return;

        if (Store.isSpider) {
            //&& !Store.cardHighlighting
            if (card.place.cardPlace === GamePlace.TABLEAU) {
                this.columns[card.place.value].showHighlight(card);
            }
        }
    }
    getMoveDiff() {
        return new Point(
            this.currentPointerPosition.x - this.firstPointerPosition.x,
            this.currentPointerPosition.y - this.firstPointerPosition.y
        );
    }
    onPointerup() {
        this.dropCard(false);
    }
    dropCard(autoPlay = false) {
        this.dragActive = autoPlay;
        this.stopDragSound();
        const diff = this.getMoveDiff();
        const isClick = autoPlay || this._isClick(diff, this.startMoveTime);
        this.startMoveTime = 0;
        removeEventListener('pointermove', this.dragCard);
        removeEventListener('pointerup', this.onPointerup);
        const firstCard = this.moveGroup[0];
        if (!firstCard) {
            return;
        }
        if (this.gameStrategy) {
            this.gameStrategy.handleCheckMoveGroupForPair(this.moveGroup, isClick, autoPlay);
            return;
        }

        const places = isClick ? this.getClickPlace() : this.getDropPlace();
        const validPlace = this.getBestValidDropPlace(places, this.moveGroup);
        let targetPosition = new Point();
        this.updateMoveGroupPosition();

        if (validPlace) {
            const { place, dropOnCard } = validPlace;
            const newZIndex = dropOnCard ? dropOnCard.place.zIndex + 1 : COLUMN_Z_INDEX;
            switch (place.place) {
                case GamePlace.TABLEAU:
                    targetPosition = this.getColumnLastCardPosition(place.dropColumnIndex);
                    break;
                case GamePlace.FOUNDATION:
                    targetPosition = Layout.getFoundationPosition(place.dropColumnIndex);
                    break;
                case GamePlace.FREECELL:
                    targetPosition = Layout.getFreeCellPosition(place.dropColumnIndex);
                    break;
            }
            this.moves.add(this.moveGroup, Store.isSpider && autoPlay);

            GameEvent.emit(EventName.ADD_SCORE, { from: firstCard.place.cardPlace, to: place.place });

            moveSound(place.place, { autocomplete: this.autocompleteActivated, autoPlay });

            this.moveGroup.forEach((card, index) => {
                this.removeCardFromBoard(card);

                card.setPlace({
                    cardPlace: place.place,
                    value: place.dropColumnIndex,
                    zIndex: newZIndex,
                    position:
                        place.place === GamePlace.TABLEAU
                            ? this.getColumnNextCardPosition(place.dropColumnIndex, index)
                            : targetPosition,
                    order: 0,
                });
            });

            this.placeMoveGroup(true, autoPlay);
            if (this.stockPile)
                this.stockPile.checkDisabled(this.stockPile.length + (this.wastePile ? this.wastePile.length : 0));
            if (Store.isSpider) {
                this.refreshAllHighlight();
            }
            if (Store.tutorialActive) this.tutorialManager.onValidMove();
        } else {
            let putOnEmpty = true;
            if (isClick) {
                this.moveGroup.forEach((card) => {
                    card.shakeCard();
                });
                if (Store.isSpider) {
                    //&& !Store.cardHighlighting
                    this.columns[this.moveGroup[0].place.value].showHighlight(firstCard);
                }
                putOnEmpty = false;
            }

            //isClick 300
            // prevent sound overlay when card dropped immediately after drag(invalid move)
            SoundManager.play(putOnEmpty ? 'cardPutEmptySFX' : 'missCardSFX', { delay: isClick ? 150 : 50 });
            this.placeMoveGroup(false);
        }
        let lastIndex = 0;
        this.cards.forEach((card) => {
            lastIndex = Math.max(card.container.zIndex, lastIndex);
        });
        this.lastZIndex = Math.max(MOVE_Z_INDEX, lastIndex);
    }

    getBestValidDropPlace(
        places: DropCardPlace[],
        moveGroup: Card[]
    ): {
        place: DropCardPlace;
        dropOnCard: Card | null;
    } {
        let dropOnCard = null;

        const validPlaces = [] as {
            place: DropCardPlace;
            dropOnCard: Card | null;
        }[];
        for (let index = 0; index < places.length; index++) {
            const p = places[index];
            dropOnCard = this._getDropOnCard(p);
            const isValid = this._isValidMove(this.moveGroup, p);
            if (isValid) {
                validPlaces.push({ place: p, dropOnCard });
            }
        }
        const firstCard = moveGroup[0];
        if (!firstCard) return validPlaces[0];
        validPlaces.sort((a, b) => {
            const columnA = this.columns[a.place.dropColumnIndex];
            const columnB = this.columns[b.place.dropColumnIndex];

            const columnALength = columnA.getLastMoveGroup().length;
            const columnBLength = columnB.getLastMoveGroup().length;

            switch (Store.name) {
                case GameName.KLONDIKE:
                    if (Store.tutorialActive && Store.tutorialStep === 10) {
                        return a.place.dropColumnIndex == 2 ? -1 : 1;
                    }
                    if (a.place.place === b.place.place) return 0;
                    if (a.place.place === GamePlace.FOUNDATION) return -1;
                    return 1;

                case GameName.FREECELL:
                    if (a.place.place === GamePlace.FOUNDATION && a.place.place === b.place.place) {
                        return a.place.dropColumnIndex < b.place.dropColumnIndex ? -1 : 1;
                    }
                    if (a.place.place === GamePlace.FREECELL) {
                        return 1;
                    }
                    if (b.place.place === GamePlace.FREECELL) {
                        return -1;
                    }
                    if (a.place.place === GamePlace.TABLEAU && a.place.place === b.place.place) {
                        if (!a.dropOnCard) return 1; // use empty as last
                        if (!b.dropOnCard) return -1; // use empty as last
                        return a.place.dropColumnIndex < b.place.dropColumnIndex ? -1 : 1;
                    }

                    if (a.place.place === GamePlace.FOUNDATION) return -1;
                    if (b.place.place === GamePlace.FOUNDATION) return 1;
                    if (!a.dropOnCard) return 1; // use empty as last
                    if (!b.dropOnCard) return -1; // use empty as last
                    if (a.place.place === b.place.place) return 0;

                    return 1;
                case GameName.PYRAMID:
                    if (a.place.place === b.place.place) return 0;
                    if (a.place.place === GamePlace.FOUNDATION) return -1;
                    return 1;
                case GameName.SPIDER:
                    if (a.place.place === GamePlace.FOUNDATION && a.place.place === b.place.place) {
                        return a.place.dropColumnIndex < b.place.dropColumnIndex ? -1 : 1;
                    }

                    if (a.place.place === GamePlace.FOUNDATION) {
                        return -1;
                    }
                    if (b.place.place === GamePlace.FOUNDATION) {
                        return 1;
                    }

                    if (!a.dropOnCard) return 1; // use empty as last
                    if (!b.dropOnCard) return -1; // use empty as last

                    if (
                        a.dropOnCard.cardColor === firstCard.cardColor &&
                        b.dropOnCard.cardColor === firstCard.cardColor
                    )
                        // 2 the best places - go to the first on left

                        return columnALength > columnBLength ? -1 : 1;

                    if (a.dropOnCard.cardColor === firstCard.cardColor) return -1;
                    if (b.dropOnCard.cardColor === firstCard.cardColor) return 1;

                    return columnALength > columnBLength ? -1 : 1;
            }
        });
        let validPlace = validPlaces[0];
        return validPlace;
    }
    stopDragSound() {
        if (this.playDragSound) clearTimeout(this.playDragSound);
        this.moveGroup.forEach((moveCard) => {
            moveCard.dropDown();
        });
    }
    startDragSound() {
        //maybne move to card
        if (this.playDragSound) clearTimeout(this.playDragSound);
        this.playDragSound = setTimeout(() => {
            SoundManager.play('cardTakeSFX');
        }, 200);
    }
    private _isClick(diff: Point, startMoveTime: number) {
        const minDistance = Math.pow(Layout.getCardSize().width * 0.4, 2); //double move distance
        const clickRange = Math.pow(diff.x, 2) + Math.pow(diff.y, 2) < minDistance;
        const timeRange = performance.now() - startMoveTime < 400;
        return clickRange && timeRange;
    }

    private _getDropOnCard(place: DropCardPlace | null): Card | null {
        if (place === null) return null;

        switch (place.place) {
            case GamePlace.TABLEAU:
                return this.columns[place.dropColumnIndex].lastCard;
            case GamePlace.FOUNDATION:
                return this.foundations[place.dropColumnIndex].lastCard;
            default:
                return null;
        }
    }

    private _isValidMove(moveGroup: Card[], place: DropCardPlace | null): boolean {
        let validMove = false;

        if (place === null) return false;

        if (Store.tutorialActive && !this.tutorialManager.isValidMove(moveGroup, place)) {
            GameEvent.emit(EventName.TUTORIAL_EXTERNAL_VALID_MOVE);

            return false; //remove moves against tutorial
        }

        switch (place.place) {
            case GamePlace.TABLEAU:
                validMove = this.columns[place.dropColumnIndex].canDropCards(moveGroup);
                break;
            case GamePlace.FOUNDATION:
                validMove = this.foundations[place.dropColumnIndex].canDropCard(moveGroup);
                break;
            case GamePlace.FREECELL:
                validMove = this.freeCells[place.dropColumnIndex].isEmpty;
                break;
        }

        return validMove;
    }
    
    updateMoveGroupPosition() {
        this.moveGroup.forEach((card) => {
            if (card.container && card.cardDisplay.container) {
                card.container.position.x += card.cardDisplay.container.position.x;
                card.container.position.y += card.cardDisplay.container.position.y;
                card.cardDisplay.container.position.set(0, 0);
            }
        });
    }

    //are those function add something?
    getColumnLastCardPosition(dropColumnIndex: number) {
        return this.columns[dropColumnIndex].getColumnLastCardPosition();
    }
    getColumnNextCardPosition(dropColumnIndex: number, add = 0) {
        return this.columns[dropColumnIndex].getCardPosition(this.columns[dropColumnIndex].length + add);
    }
    getColumnCardPosition(dropColumnIndex: number, index: number) {
        return this.columns[dropColumnIndex].getCardPosition(index);
    }

    placeMoveGroup(changePlace = false, autoPlay = false, disableBlock = false, skipAnimation = false) {
        let targetPlace = this.moveGroup[0].place.cardPlace;
        if (Store.isSpider && targetPlace === GamePlace.FOUNDATION) {
            this.moveGroup.reverse();
            setTimeout(() => {
                this.columns.forEach((col) => {
                    col.refresh();
                });
            }, 650);
            this.moveGroup.forEach((card, index) => {
                setTimeout(() => {
                    card.takeUp();
                }, index * 50 + 100);
            });
        } else {
            this.columns.forEach((col) => {
                col.revealLastCard();
            });
        }

        let cardMoveSpeed = CARD_MOVE_TIME;

        switch (Store.name) {
            case GameName.SPIDER:
                cardMoveSpeed = autoPlay ? AUTOCOMPLETE_MOVE_TIME : CARD_MOVE_TIME;
                break;
            case GameName.KLONDIKE:
            case GameName.FREECELL:
                if (this.autoPlayMoveCount > 0) {
                    cardMoveSpeed = Math.max(CARD_MOVE_TIME, AUTOCOMPLETE_MOVE_TIME);
                }
                break;
        }

        this.moveGroup.forEach((card, index) => {
            card.setActive(false);
            const lastMove = index === this.moveGroup.length - 1;

            if (changePlace) {
                switch (card.place.cardPlace) {
                    case GamePlace.TABLEAU:
                        this.columns[card.place.value].addCard(card);
                        break;
                    case GamePlace.WASTE_PILE:
                        this.wastePile?.addCard(card);
                        break;

                    case GamePlace.FOUNDATION:
                        this.foundations[card.place.value].addCard(card);
                        break;
                    case GamePlace.STOCKPILE:
                        this.stockPile.putOnTop(card);
                        break;
                    case GamePlace.FREECELL:
                        this.freeCells[card.place.value].addCard(card);
                        break;
                }
            }
            setTimeout(() => {
                if (changePlace && card.place.cardPlace === GamePlace.FOUNDATION) {
                    this.foundations[card.place.value].beforeCardAdded();
                }
            }, AUTOPLAY_DELAY);

            if (!skipAnimation) {
                this.moveCardIntoPlace(card, index, cardMoveSpeed, changePlace, autoPlay, lastMove);
            }
        });

        if (!disableBlock) {
            this.blockMove((this.moveGroup.length * CARD_GROUP_DELAY_TIME + cardMoveSpeed) * 1000);
        }

        this.moveGroup = [];
        Store.setGameState(this);
    }

    moveCardIntoPlace(card: Card, index: number, cardMoveSpeed: number, changePlace: boolean, autoPlay: boolean, lastMove: boolean, fastMove: boolean = false) {
        if (fastMove) {
            card.container.position = card.place.position;
            return;
        }
        
        card.moveTo(
            card.place.position,
            {
                delay: index * CARD_GROUP_DELAY_TIME,
                duration: cardMoveSpeed,
            },
            () => {
                if (
                    changePlace &&
                    card.place.cardPlace === GamePlace.FOUNDATION &&
                    this.foundations[card.place.value]
                ) {
                    this.foundations[card.place.value].cardAdded(card);
                }
                if (lastMove) {
                    this.refreshBoard();
                    if (autoPlay) {
                        if (this.autoPlayActivated) GameEvent.emit(EventName.AUTOPLAY_MOVE_FINISHED);
                        else if (this.autocompleteActivated) GameEvent.emit(EventName.AUTOCOMPLETE_MOVE_FINISHED);
                    }
                }
            },
            () => {
                //on move start
                if (Store.isSpider && changePlace && card.place.cardPlace === GamePlace.FOUNDATION) {
                    SoundManager.play('cardPutSFX');
                }
            }
        );
    }

    getClickPlace(): DropCardPlace[] {
        console.log('[PYRAMID CLICK] Getting click places');
        
        if (this.gameStrategy) {
            return this.gameStrategy.getAvailableClickPlace();
        }
        
        const options = [] as DropCardPlace[];
        if (Store.isFreecell) {
            if (this.availableFreeCells < this.moveGroup.length - 1) return options;
        }
        const firstCard = this.moveGroup[0];
        if (!firstCard) return [];

        const currentPlace = firstCard.place;
        if (currentPlace.cardPlace !== GamePlace.FOUNDATION) {
            for (let f = 0; f < this.foundations.length; f++) {
                const foundation = this.foundations[f];
                if (foundation.canDropCard(this.moveGroup)) options.push(foundation.getPlace());
            }
        }

        const skipColumn = currentPlace.cardPlace === GamePlace.TABLEAU ? currentPlace.value : -1;

        for (let c = 0; c < this.columns.length; c++) {
            const column = this.columns[c];
            if (c !== skipColumn) {
                if (column.canDropCards(this.moveGroup)) options.push({ place: GamePlace.TABLEAU, dropColumnIndex: c });
            }
        }

        if (this.moveGroup.length === 1) {
            for (let index = 0; index < this.freeCells.length; index++) {
                const freeCell = this.freeCells[index];
                if (freeCell.isEmpty) options.push(freeCell.getPlace());
            }
        }

        return options;
    }

    getDropPlace(): DropCardPlace[] {
        let possibleDropPositions = [] as DropCardPlace[];
        if (Store.isFreecell) {
            if (this.availableFreeCells < this.moveGroup.length - 1) return [];
        }

        const firstCard = this.moveGroup[0];
        if (!firstCard) return [];
        const dragIds = this.moveGroup.map((c) => c._id);
        for (let index = 0; index < this.foundations.length; index++) {
            const foundation = this.foundations[index];
            const container = foundation.lastCard ? foundation.lastCard.container : foundation.placeholder;

            if (foundation.lastCard && dragIds.includes(foundation.lastCard?._id)) continue;

            if (areBoxesCollide(container, firstCard.container))
                possibleDropPositions.push({ place: GamePlace.FOUNDATION, dropColumnIndex: index });
        }
        for (let index = 0; index < this.columns.length; index++) {
            const column = this.columns[index];
            if (column.lastCard && dragIds.includes(column.lastCard?._id)) continue;

            const cardContainer = column.placeholder;
            if (areBoxesCollide(cardContainer, firstCard.container))
                possibleDropPositions.push({ place: GamePlace.TABLEAU, dropColumnIndex: index });

            column.cards.forEach((card) => {
                if (areBoxesCollide(firstCard.container, card.container))
                    possibleDropPositions.push({ place: GamePlace.TABLEAU, dropColumnIndex: index });
            });
        }
        if (this.moveGroup.length === 1) {
            for (let index = 0; index < this.freeCells.length; index++) {
                const cell = this.freeCells[index];
                if (!cell.isEmpty) continue;
                const container = cell.placeholder;
                if (areBoxesCollide(container, firstCard.container))
                    possibleDropPositions.push({ place: GamePlace.FREECELL, dropColumnIndex: index });
            }
        }
        return possibleDropPositions;
    }

    start(): void {
        addToLevel(this.gameContainer);
        GameEvent.add(EventName.OPEN_SETTINGS, () => {
            if (!this.cards.length) return;
            this.refreshBoard();
        });
        GameEvent.add(EventName.HIDE_SETTINGS, () => {
            if (!this.cards.length) return;
            this.refreshBoard();
        });
    }

    newGame(difficulty: DifficultyLevelType | null, onRefresh = false, onWinPopup = false) {
        const lastGameId = Store.getPreviousGameId(difficulty || Store.difficulty);
        if (!Store.tutorialViewed[Store.name] && !Store.gameWins[Store.name]) {
            this.tutorialManager.showWelcomeMessage();
        }
        //const lastGameId = null;
        if (lastGameId && onRefresh) {
            this.newGameById(lastGameId, true);
            return lastGameId;
        } else {
            if (!onRefresh) {
                this.logNewGameTriggered(onWinPopup);
            }
            this.newGameById(Store.gameId);
            return Store.gameId;
        }
    }
    logNewGameTriggered(onWinPopup = false) {
        const lastGame = Store.getPreviousGame();

        const oldDifficulty = Store.getDifficultyText(lastGame.difficulty as DifficultyLevelType);
        const oldType =
            lastGame.name === GameName.KLONDIKE
                ? Store.getDealCountText(lastGame.difficulty as DifficultyLevelType)
                : Store.getSuitCountText(lastGame.difficulty as DifficultyLevelType);
        const oldName = lastGame.name;

        GAEvent(onWinPopup ? GAEventNames.WIN_POPUP_NEW_GAME_CLICK : GAEventNames.NEW_GAME_CLICK, {
            type: oldType,
            difficulty: oldDifficulty,
            name: oldName,
            nameSelected: Store.name,
            typeSelected: Store.getGameTypeText(),
            difficultiesSelected: Store.getDifficultyText(),
        });
    }
    async newGameById(suitId: string, loadTable = false) {
        const lastGame = Store.getPreviousGame();
        Store.loadAdsCounter();
        const newGame = lastGame.gameId !== suitId;
        Store.gameId = suitId;

        this.resetGame();


        const difficultiesList = getDifficultiesList();
        const targetDifficulty = difficultiesList[lastGame.difficulty as DifficultyLevelType];
        
        if (!targetDifficulty) {
            Store.setDifficulty(getFirstDifficulty(), Store.difficultyBalanced);
        }
        
        const suit = await getSuit(suitId);

        if (!suit) return;
        const { difficulty, cards } = suit;

        Store.difficulty = difficulty;

        if (newGame) {
            Store.attempts = 0;
            Store.magicWandUsed = 0;
        }
        if (!loadTable) SoundManager.play('newGameSFX');
        Store.setPreviousGame();

        GameEvent.emit(EventName.HIDE_LOADER);
        GameEvent.emit(EventName.NEW_GAME);
        GameEvent.emit(EventName.SHOW_GAME);

        Store.reset();
        this.firstMove = false; // reset firstMove after joining to the new game
        // this.drawCardsOnBoard(cards, loadTable);

        const lastTable = Store.gameState;
        this.removeGameCards();
        addEventListener('keydown', this.onKeyDown);

        if (Store.tutorialActive) {
            GameEvent.emit(EventName.START_TUTORIAL);
        } else if (loadTable && lastTable.gameId) {
            loadCardsTable(this, () => {
                setTimeout(() => {
                    GameEvent.emit(EventName.GAME_INTRO_COMPLETED);
                    GameEvent.emit(EventName.UNBLOCK_KEYBOARD);
                }, 100);
                getSuitWinScore();
            });

            if (this.gameStrategy) {
                this.gameStrategy.reDraw();
                this.gameStrategy.handleChangeHighlight();
            }

        } else {
            if (this.gameStrategy) {
                this.gameStrategy.draw(cards, () => {
                    this.refreshBoard();
                    GameEvent.emit(EventName.GAME_INTRO_COMPLETED);
                    GameEvent.emit(EventName.UNBLOCK_KEYBOARD);
                    addEventListener('keydown', this.onKeyDown);
                    getSuitWinScore();
                });
            } else {
                drawCardsIntro(this, cards, () => {
                    GameEvent.emit(EventName.GAME_INTRO_COMPLETED);
                    GameEvent.emit(EventName.UNBLOCK_KEYBOARD);
                    addEventListener('keydown', this.onKeyDown);
                    getSuitWinScore();
                });
            }
        }

        if (!Store.assetLoaded) {
            await lateAssetLoad();
            // Only preload sounds if sound is enabled
            if (Store.soundOn) {
                preloadGameSounds();
            }
            GameEvent.emit(EventName.SHOW_UI);
            GameEvent.emit(EventName.GAME_READY);
        }

        GameLog('GAME ID:', Store.gameId, Store.difficulty);
    }

    removeGameCards() {
        while (this.cards.length) {
            const card = this.cards.shift();
            card?.destroy();
        }
    }

    startTutorial() {
        this.resetGame();
        // fill Store.gameState
        this.tutorialManager.createTutorialView();
        addEventListener('keydown', this.onKeyDown);
        Store.startGame();
    }

    public resetGame() {
        this.blockMove(0);
        this.autoPlayActivated = false;
        this.autocompleteActivated = false;
        this.autocompleteWorker.reset();
        this.gameOver = false;
        // Restart counters of time, moves, and score

        removeEventListener('keydown', this.onKeyDown);
        Store.reset();
        GameEvent.emit(EventName.RESET_GAME);
        GameEvent.emit(EventName.SET_UNDO_DISABLED, true);
        this.removeGameCards();
        // Clear all variables from the previous game (clean the board)
        while (this.columns.length) this.columns.pop()?.destroy();
        while (this.foundations.length) this.foundations.pop()?.destroy();
        while (this.freeCells.length) this.freeCells.pop()?.destroy();

        this.columns = [];
        if (this.stockPile) this.stockPile.reset();
        if (this.wastePile) this.wastePile.reset();

        this.moveGroup = [];
        this.moves.removeAll();
    }

    removeCardFromBoard(card: Card) {
        console.log(`[REMOVE CARD] Removing card ${card.code}(${card.value}) from board`);
        
        this.columns.forEach((column, index) => {
            const beforeCount = column.cards.length;
            column.removeCard(card);
            const afterCount = column.cards.length;
            if (beforeCount !== afterCount) {
                console.log(`[REMOVE CARD] Removed card ${card.code} from column ${index} (${beforeCount} -> ${afterCount} cards)`);
            }
        });
        
        this.freeCells.forEach((cell, index) => {
            const beforeCount = cell.cards.length;
            cell.removeCard(card);
            const afterCount = cell.cards.length;
            if (beforeCount !== afterCount) {
                console.log(`[REMOVE CARD] Removed card ${card.code} from free cell ${index} (${beforeCount} -> ${afterCount} cards)`);
            }
        });
        
        const stockpileBefore = this.stockPile.cards.length;
        this.stockPile.removeCard(card);
        const stockpileAfter = this.stockPile.cards.length;
        if (stockpileBefore !== stockpileAfter) {
            console.log(`[REMOVE CARD] Removed card ${card.code} from stockpile (${stockpileBefore} -> ${stockpileAfter} cards)`);
        }
        
        this.foundations.forEach((foundation, index) => {
            const beforeCount = foundation.cards.length;
            foundation.removeCard(card);
            const afterCount = foundation.cards.length;
            if (beforeCount !== afterCount) {
                console.log(`[REMOVE CARD] Removed card ${card.code} from foundation ${index} (${beforeCount} -> ${afterCount} cards)`);
            }
        });
        
        if (this.wastePile) {
            const wasteBefore = this.wastePile.cards.length;
            this.wastePile.removeCard(card);
            const wasteAfter = this.wastePile.cards.length;
            if (wasteBefore !== wasteAfter) {
                console.log(`[REMOVE CARD] Removed card ${card.code} from waste pile (${wasteBefore} -> ${wasteAfter} cards)`);
            }
        }
    }
    
    refreshBoard() {
        this.dragActive = false;
        if (this.moveGroup.length) {
            return;
        }

        this.stockPile.checkDisabled(this.stockPile.length + (this.wastePile ? this.wastePile.length : 0));
        if (this.gameStrategy) {
            this.gameStrategy.refreshBoardState();
            this.gameStrategy.handleChangeHighlight();
        } else {
            for (let col = 0; col < this.columns.length; col++) {
                this.columns[col].refresh();
            }
        }
        for (let cell = 0; cell < this.freeCells.length; cell++) {
            this.freeCells[cell].refresh();
        }

        this.foundations.forEach((foundation) => {
            foundation.refresh();
        });

        this.wastePile?.refresh(CARD_MOVE_TIME);
        this.stockPile.refresh();

        if (this.magicWand.active) return;

        if (!this.autocompleteActivated) this.autocompleteWorker.isAvailable();
        this.magicWand.isAvailable();
        this.checkWinCondition();
        if (this.gameOver) {
            Store.resetTable();
        } else {
            Store.setGameState(this);
        }
    this.tutorialManager.refreshBoard();
    }
    
    refreshAllHighlight() {
        this.columns.forEach((col) => {
            col.showHighlight(col.lastCard, true);
        });
    }
    
    checkWinCondition() {
        if (Store.tutorialActive) return;
        if (!this.foundations.length) return;
        if (this.gameOver) return;


        async function sendMissionAction() {
            Store.increaseContextValue('wins').then((context) => {

                const missionActionData: MissionActionData = { type: ActionType.WINS, payload: {
                    gameName: Store.name,
                    difficulty: Store.difficulty,
                    localHints: Store.hints,
                    localMagicWandUses: Store.magicWandUsed,
                    localUndos: Store.undos,
                    localDeckTurns: Store.deckTurns,
                    localMoves: Store.movesCount,
                    localTime: Store.time,
                    ...context,
                }, currentProgress: context.wins };

                GameEvent.emit(EventName.MISSION_ACTION, missionActionData);
            });
        }

        if (this.gameStrategy) {
            const result = this.gameStrategy.checkWinCondition();
            
            if (result) {
                this.gameOver = true;
                this.moves.checkButtonState();
                Store.winGame();
                sendMissionAction();
                this.showWinScreen();
                logWinGame(this.autocompleteWorker.autocompleteButton.status);
            }
            return;
        }

        for (let foundationIndex = 0; foundationIndex < this.foundations.length; foundationIndex++) {
            const foundation = this.foundations[foundationIndex];
            if (!foundation.isComplete) return;
        }

        this.gameOver = true;

        this.moves.checkButtonState();
        Store.winGame();
        sendMissionAction();
        this.showWinScreen();
        logWinGame(this.autocompleteWorker.autocompleteButton.status);
    }
    showWinScreen() {
        Store.resetTable();
        const currentTickets = parseInt(localStorage.getItem('stories_tickets_counter') || '0');
        localStorage.setItem('stories_tickets_counter', (currentTickets + this.winScreen.ticketByDifficulty()).toString());

        setTimeout(
            () => {
                this.winScreen.showWinScreen();
            },
            Store.isSpider ? WIN_POPUP_SPIDER_DELAY : WIN_POPUP_DELAY
        );
    }

    blockMove(dealLimit: number) {
        this.moveBlocked = performance.now() + dealLimit;
        GameEvent.emit(EventName.BLOCK_ACTION_BUTTONS, dealLimit);
    }
    get isMoveBlocked() {
        return this.moveBlocked > performance.now();
    }

    autoPlay() {
        // this.refreshBoard();
        if (this.autocompleteActivated) return;
        let autoPlayActive = false;
        // this.autoPlayActivated = false;
        if (Store.hasAutoPlay) {
            this.autoPlayMoveCount++;

            const possibleCards = this.foundations.map((f) => f.lastCard);
            const lastCards = [] as Card[];
            this.freeCells.forEach((fr) => {
                if (fr.lastCard) lastCards.push(fr.lastCard);
            });
            if (this.wastePile?.lastCard) lastCards.push(this.wastePile?.lastCard);

            this.columns.forEach((col) => {
                if (col.lastCard) lastCards.push(col.lastCard);
            });

            for (let c = 0; c < lastCards.length; c++) {
                if (autoPlayActive) break;

                const lastCard = lastCards[c];
                lastCard.setActive(true);
                if (lastCard.value === ACES_VALUE) {
                    lastCard.triggerClick();
                    autoPlayActive = true;
                    break;
                }
                if (autoPlayActive) break;
                //for possibleCards
                for (let p = 0; p < possibleCards.length; p++) {
                    const foundationCard = possibleCards[p];
                    if (foundationCard && foundationCard.canDropInFoundation(lastCard)) {
                        lastCard.triggerClick();
                        autoPlayActive = true;
                        break;
                    }
                }
            }
        }
        if (!autoPlayActive) this.autoPlayMoveCount = 0;
        this.autoPlayActivated = autoPlayActive;
    }
    get availableFreeCells() {
        return this.freeCells.filter((cell) => cell.isEmpty).length;
    }

    getCardBoardPlace(matchCard: Card): GameBoardPlace {
        switch (matchCard.place.cardPlace) {
            case GamePlace.TABLEAU:
                return this.columns[matchCard.place.value];
            case GamePlace.FOUNDATION:
                return this.foundations[matchCard.place.value];
            case GamePlace.FREECELL:
                return this.freeCells[matchCard.place.value];
            case GamePlace.WASTE_PILE:
                return this.wastePile as GameBoardPlace;
            case GamePlace.STOCKPILE:
                return this.stockPile;
        }
    }

    async dropMagicCards(items: MagicItem[], onFinish?: () => void) {
        this.dragActive = true;
        this.startMoveTime = 0;
        let soundPlayed = false;
        let currentTimeout: number | undefined = undefined;

        const addToFoundation = async (card: Card, to: GameBoardPlace, targetCard: Card | null, place: MagicPlace, isLast: boolean) => {
            if (!card) return;
            
            this.moveGroup = [card];

            GameEvent.emit(EventName.ADD_SCORE, { from: card.place.cardPlace, to: to.gamePlace });

            let newPosition = new Point();

            if (to.gamePlace === GamePlace.FOUNDATION) {
                newPosition = this.foundations[to.index].container.position;
                this.foundations[to.index].addCard(card);
            } else {
                const column = this.columns[to.index];
                if (targetCard === null) {
                    column.addCard(card);
                    newPosition = this.getColumnCardPosition(to.index, 0);
                } else {
                    let positionIndex = 0;
                    if (place === MagicPlace.AFTER) {
                        positionIndex = column.addCardAfter(targetCard, card);
                        newPosition = this.getColumnCardPosition(to.index, positionIndex);
                    } else {
                        positionIndex = column.addCardBefore(targetCard, card);
                        newPosition = this.getColumnCardPosition(to.index, positionIndex);
                        newPosition.y -= Layout.getCardSize().height * 0.9;
                    }
                }
            }
            card.place.cardPlace = to.gamePlace;
            card.place.value = to.index;
            card.place.position = newPosition;

            card.setActive(true);
            if (to.gamePlace === GamePlace.FOUNDATION) {
                clearTimeout(currentTimeout);
                currentTimeout = setTimeout(() => {
                    this.foundations[to.index].beforeCardAdded();
                }, AUTOPLAY_DELAY);
            }
            this.moveGroup = [];

            gsap.to(card.container.position, {
                x: newPosition.x,
                y: newPosition.y,
            }).then(() => {
                if (!soundPlayed) {
                    moveSound(to.gamePlace, { autoPlay: true, autocomplete: false });
                    soundPlayed = true;
                }

                // Ensure full reflow of the target column after inserting into the middle
                // so cards below shift correctly (important for Spider after Magic Wand)
                this.columns[to.index].refresh();

                card.container.zIndex = card.place.zIndex;
                // moveCard.place.position.set(newPosition.x, newPosition.y);
                // moveCard.reset();
                this.magicWand.active = false;
                const timeout = 100;

                this.blockMove(timeout);
                GameEvent.emit(EventName.BLOCK_WAND_AND_HINTS, timeout);
                if (Store.isSpider) {
                    this.refreshAllHighlight();
                }
                this.dragActive = false;

                if (isLast) {
                    setTimeout(() => {
                        onFinish?.();
                    }, timeout);
                }
            }); 
        }

        for (let i = 0; i < items.length; i++) {
            const { moveCard, to, place, targetCard } = items[i];

            await addToFoundation(moveCard, to, targetCard, place, i === items.length - 1);
        }
    }
}
